import React from "react";
import { MapPin } from "lucide-react";

interface City {
  name: string;
  zip_code: string;
  latitude: number;
  longitude: number;
  department: string;
  distance_km: number;
}

interface GeographicExpanderProps {
  reference?: string;
  radius_km?: number;
  center_coordinates?: {
    latitude: number;
    longitude: number;
  };
  total_cities?: number;
  cities_found?: City[];
  error?: string;
}

export const GeographicExpanderResult: React.FC<GeographicExpanderProps> = ({
  reference,
  radius_km,
  center_coordinates,
  total_cities,
  cities_found = [],
  error,
}) => {
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (!center_coordinates) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        Aucune donnée géographique disponible
      </div>
    );
  }

  // Build static map URL via server-side proxy (keeps API key server-side)
  const { latitude, longitude } = center_coordinates;
  const zoom =
    radius_km && radius_km > 100 ? 8 : radius_km && radius_km > 50 ? 9 : 10;

  const mapParams = new URLSearchParams({
    center: `${latitude},${longitude}`,
    zoom: String(zoom),
    size: "600x300",
    maptype: "roadmap",
  });
  mapParams.append("markers[]", `color:red|${latitude},${longitude}`);
  if (cities_found.length > 0) {
    const cityMarkers = cities_found
      .slice(0, 10)
      .map((city) => `${city.latitude},${city.longitude}`)
      .join("|");
    mapParams.append("markers[]", `color:blue|size:small|${cityMarkers}`);
  }
  const staticMapUrl = `/app/static_map?${mapParams.toString()}`;

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-gray-600" />
            <div>
              <div className="font-medium text-gray-900">{reference}</div>
              {radius_km && (
                <div className="text-xs text-gray-500">
                  Rayon: {radius_km}km - {total_cities} ville
                  {total_cities && total_cities > 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Static Map */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <img
            src={staticMapUrl}
            alt={`Carte de ${reference}`}
            className="h-auto w-full"
            loading="lazy"
          />
        </div>

        {/* Cities List */}
        {cities_found.length > 0 && (
          <div className="flex flex-col space-y-4 mt-4">
            <div className="text-sm font-medium text-slate-700">
              Villes principales
            </div>
            <div className="space-y-2">
              {cities_found.slice(0, 5).map((city, index) => (
                <div
                  key={index}
                  className="px-4 py-2 border rounded-lg text-sm bg-slate-50/70 border-slate-300/70 flex justify-between items-center"
                >
                  <div>
                    <div className="font-medium capitalize text-slate-700">
                      {city.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {city.zip_code} - {city.department}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] text-slate-600 rounded-full border border-slate-300/70 bg-white py-0.5 px-2">
                      {city.distance_km.toFixed(0)}km
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
